import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Button,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

// Countdown Timer Component
const CountdownTimer = ({ endTime }) => {
    const [timeLeft, setTimeLeft] = useState({
        hours: '00',
        minutes: '00',
        seconds: '00'
    });

    const calculateTimeLeft = useCallback(() => {
        const now = new Date().getTime();
        const difference = endTime - now;

        if (difference <= 0) {
            return { hours: '00', minutes: '00', seconds: '00' };
        }

        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        return {
            hours: hours.toString().padStart(2, '0'),
            minutes: minutes.toString().padStart(2, '0'),
            seconds: seconds.toString().padStart(2, '0')
        };
    }, [endTime]);

    useEffect(() => {
        if (!endTime) return;

        // Initial calculation
        setTimeLeft(calculateTimeLeft());

        // Update every second
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [endTime, calculateTimeLeft]);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mb: 2,
            backgroundColor: 'rgba(0,0,0,0.2)',
            padding: '6px 16px',
            borderRadius: '30px',
            backdropFilter: 'blur(2px)'
        }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                OFFER ENDS IN:
            </Typography>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 700,
                fontSize: '1.2rem'
            }}>
                <Box sx={{
                    backgroundColor: 'white',
                    color: '#4a6fa5',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    minWidth: '26px',
                    textAlign: 'center'
                }}>
                    {timeLeft.hours}
                </Box>
                <Typography sx={{ color: 'white' }}>:</Typography>
                <Box sx={{
                    backgroundColor: 'white',
                    color: '#4a6fa5',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    minWidth: '26px',
                    textAlign: 'center'
                }}>
                    {timeLeft.minutes}
                </Box>
                <Typography sx={{ color: 'white' }}>:</Typography>
                <Box sx={{
                    backgroundColor: 'white',
                    color: '#4a6fa5',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    minWidth: '26px',
                    textAlign: 'center'
                }}>
                    {timeLeft.seconds}
                </Box>
            </Box>
        </Box>
    );
};

const Slider = ({ sliders }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentSlide, setCurrentSlide] = useState(0);
    const [displaySliders, setDisplaySliders] = useState([]);

    useEffect(() => {
        // Precompute end times and handle slide duplication
        const processedSlides = sliders.map(slide => {
            if (slide.date_start_active && slide.count_hours_active) {
                const startTime = new Date(slide.date_start_active);
                const endTime = new Date(startTime.getTime() + slide.count_hours_subscriptions * 3600000);
                return { ...slide, endTime: endTime.getTime() };
            }
            return slide;
        });

        if (processedSlides.length === 1) {
            setDisplaySliders([
                ...processedSlides,
                { ...processedSlides[0], id: 'duplicate' }
            ]);
        } else {
            setDisplaySliders(processedSlides);
        }
    }, [sliders]);

    useEffect(() => {
        if (displaySliders.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentSlide(prev => (prev === displaySliders.length - 1 ? 0 : prev + 1));
        }, 5000);

        return () => clearInterval(interval);
    }, [displaySliders.length]);

    const nextSlide = () => {
        if (displaySliders.length <= 1) return;
        setCurrentSlide(prev => (prev === displaySliders.length - 1 ? 0 : prev + 1));
    };

    const prevSlide = () => {
        if (displaySliders.length <= 1) return;
        setCurrentSlide(prev => (prev === 0 ? displaySliders.length - 1 : prev - 1));
    };

    console.log(displaySliders)

    if (sliders.length === 0) return null;

    return (
        <Box sx={{
            maxWidth: `${window.screen.width < 1200 ? '90%' : '1200px'}`,
            margin: '40px auto auto auto',
            padding: isMobile ? '0 16px' : '0',
            width:'100%',
            fontFamily: '"Poppins", sans-serif'
        }}>
            <Box sx={{
                position: 'relative',
                height: isMobile ? 'auto' : '300px',
                width:'100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#fff'
            }}>
                {/* Navigation arrows - only show if more than one slide */}
                {displaySliders.length > 1 && (
                    <>
                        <IconButton
                            onClick={prevSlide}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 20,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                color: '#2C3E50',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                        >
                            <KeyboardArrowLeft fontSize="large" />
                        </IconButton>

                        <IconButton
                            onClick={nextSlide}
                            sx={{
                                position: 'absolute',
                                right: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 20,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                color: '#2C3E50',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                        >
                            <KeyboardArrowRight fontSize="large" />
                        </IconButton>
                    </>
                )}

                {/* Slides container */}
                <Box sx={{
                    display: 'flex',
                    transition: 'transform 0.5s ease',
                    transform: `translateX(-${currentSlide * 100}%)`,
                    height: '100%',
                    width: '100%',
                }}>
                    {displaySliders.map((slide, index) => (
                        <Box
                            key={index}
                            sx={{
                                flex: '0 0 100%',
                                height: isMobile ? '500px' : '100%',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                width:'100%'
                            }}
                        >
                            {/* Image container (left) */}
                            <Box sx={{
                                width: isMobile ? '100%' : '50%',
                                height: isMobile ? '50%' : '100%',
                                backgroundImage: `url(${slide.logo})`,
                                backgroundSize: isMobile ? 'cover' : 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                position: 'relative',
                                '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    zIndex: 1
                                }
                            }} />

                            {/* Content container (right) */}
                            <Box sx={{
                                width: isMobile ? '100%' : '50%',
                                height: isMobile ? '50%' : '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                padding: isMobile ? '20px' : '40px',
                                backgroundColor: '#4a6fa5',
                                color: 'white',
                                boxSizing: 'border-box',
                                position: 'relative'
                            }}>
                                {/* Countdown Timer */}
                                {/*{slide.endTime && (*/}
                                    <CountdownTimer endTime={new Date(new Date(slide.date_start_active).getTime() + slide.count_hours_subscriptions * 3600000)} />
                                {/*)}*/}

                                <Typography variant="h3" sx={{
                                    fontWeight: 700,
                                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                                    mb: 1,
                                    lineHeight: 1.2
                                }}>
                                    {slide.title}
                                </Typography>

                                <Typography variant="body1" sx={{
                                    fontSize: isMobile ? '1rem' : '1.1rem',
                                    mb: 3,
                                    maxWidth: '500px'
                                }}>
                                    {slide.description}
                                </Typography>

                                <Button
                                    variant="contained"
                                    onClick={() => window.open(slide.last_minute_url, '_blank')}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#eca601',
                                        fontWeight: 700,
                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                        padding: '10px 30px',
                                        borderRadius: '30px',
                                        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    {slide.last_minute_button ?? "Visit the Site"}
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Slider indicators - only show if more than one slide */}
                {displaySliders.length > 1 && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '10px',
                        zIndex: 20
                    }}>
                        {sliders.map((_, index) => (
                            <Button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                sx={{
                                    minWidth: '12px',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    padding: 0,
                                    backgroundColor: currentSlide === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                    '&:hover': {
                                        backgroundColor: currentSlide === index ? 'white' : 'rgba(255, 255, 255, 0.7)'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Slider;